import type { ReactNode } from 'react';
import { T } from 'gt-react';
import { TaskReviewPageLayout } from './TaskReviewPageLayout';
import { TaskReviewRailLayout } from './TaskReviewRailLayout';
import {
  useTaskReviewModel,
  type TaskReviewLoadState,
} from './useTaskReviewModel';

export type TaskReviewLayout = 'page' | 'rail';

/**
 * Shared task review: loads one `core_tasks` row and renders either the
 * full-page two-column layout or the compact collapsible rail.
 */
export function TaskReviewBody({
  taskId,
  fromPage,
  layout,
  trailing,
  openChat = false,
}: {
  taskId: string;
  fromPage?: string;
  layout: TaskReviewLayout;
  trailing?: ReactNode;
  /** Open Nami's task chat on load (`?chat=open` deep link). Page layout only. */
  openChat?: boolean;
}) {
  const model = useTaskReviewModel({ taskId, fromPage, trailing });
  if (model.kind !== 'ready') {
    return <TaskReviewLoadNotice state={model.kind} />;
  }
  if (layout === 'rail') {
    return <TaskReviewRailLayout {...model.loaded} />;
  }
  return <TaskReviewPageLayout {...model.loaded} openChat={openChat} />;
}

/** Loading, error and not-found states for a task view. */
export function TaskReviewLoadNotice({
  state,
}: {
  state: Exclude<TaskReviewLoadState, 'ready'>;
}) {
  const notice = LOAD_NOTICES[state];
  return (
    <div
      className={`flex h-full items-center justify-center text-sm ${notice.className}`}
    >
      {notice.message}
    </div>
  );
}

const LOAD_NOTICES: Record<
  Exclude<TaskReviewLoadState, 'ready'>,
  { className: string; message: ReactNode }
> = {
  loading: {
    className: 'text-muted-foreground',
    message: <T context="loading state for the task detail page">Loading…</T>,
  },
  error: {
    className: 'text-destructive',
    message: (
      <T context="error state when the task detail query failed">
        Could not load this task.
      </T>
    ),
  },
  missing: {
    className: 'text-destructive',
    message: (
      <T context="empty state when the task id does not exist">
        Task not found.
      </T>
    ),
  },
};
